import React, { useState, useEffect } from 'react';
import { X, Plus, Pencil, Play, Pause } from 'lucide-react';

// Character limits for Instagram ads
const HEADLINE_MAX_CHARS = 30;
const DESCRIPTION_MAX_CHARS = 500;

const InstagramFeedPreview = ({ 
  campaignPreview, 
  user, 
  hasCatalog, 
  mediaUrl, 
  hasMedia, 
  loadingMedia: _loadingMedia, 
  uploadingMedia,
  loadingImage,
  deletingMedia,
  currentImageIndex: _currentImageIndex, 
  mediaInputRef, 
  handleImageSelection, 
  removeCurrentImage,
  editingHeadline,
  editingDescription,
  editHeadlineText,
  editDescriptionText,
  isEditMode,
  allowHeadlineEdit = true,
  allowDescriptionEdit = true,
  handleEditHeadline,
  handleEditDescription,
  handleSaveHeadline,
  handleSaveDescription,
  handleCancelEdit,
  setEditHeadlineText,
  setEditDescriptionText,
  likes,
  title,
  description,
  mediaType
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [localHeadlineText, setLocalHeadlineText] = useState(editHeadlineText || '');
  const [localDescriptionText, setLocalDescriptionText] = useState(editDescriptionText || '');

  // Sync local state with props when editing starts
  useEffect(() => {
    if (editingHeadline === 0) {
      setLocalHeadlineText(editHeadlineText || '');
    }
  }, [editingHeadline, editHeadlineText]);

  useEffect(() => {
    if (editingDescription === 0) {
      setLocalDescriptionText(editDescriptionText || '');
    }
  }, [editingDescription, editDescriptionText]);

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
  };

  const handleSaveHeadlineLocal = async () => {
    // Use the local state value
    const currentValue = localHeadlineText;
    
    // Update the draft first and wait for it to complete
    await setEditHeadlineText(currentValue);
    
    // Now call the save function with the current value directly
    await handleSaveHeadline(currentValue);
  };

  const handleSaveDescriptionLocal = async () => {
    // Use the local state value
    const currentValue = localDescriptionText;
    await setEditDescriptionText(currentValue);
    await handleSaveDescription(currentValue);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg w-full max-w-xs sm:max-w-sm lg:max-w-xs mx-auto shadow-xl hover:shadow-2xl transition-shadow duration-300 flex flex-col">
      {/* Feed frame - vertical layout */}
      <div className="bg-white rounded-lg overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center p-2 border-b border-gray-100 bg-white">
            <div className="w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold text-xs">
              {(campaignPreview?.business_name || user?.business_name || 'Tu Negocio')[0] || 'B'}
            </div>
            <div className="ml-2 flex-1">
              <div className="font-semibold text-xs">{campaignPreview?.business_name || user?.business_name || 'Tu Negocio'}</div>
              <div className="text-xs text-gray-500">Publicidad</div>
            </div>
            <div className="text-gray-400">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
            </div>
          </div>

          {/* Square Image or placeholder - Fixed 1:1 aspect ratio */}
          <div 
            className={`w-full ${!hasMedia ? 'cursor-pointer' : ''}`}
            style={{ 
              aspectRatio: '1 / 1', // Force 1:1 aspect ratio
              width: '100%',
              height: 'auto',
              maxWidth: '100%'
            }}
            onClick={!hasMedia ? handleImageSelection : undefined}
          >
            {hasMedia || uploadingMedia ? (
              <div className="w-full h-full relative">
                {uploadingMedia || loadingImage || deletingMedia ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <div className="flex flex-col items-center space-y-2">
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-xs text-gray-500">
                        {deletingMedia ? 'Borrando...' : loadingImage ? 'Cambiando archivo...' : uploadingMedia ? 'Subiendo archivo...' : 'Cargando archivo...'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {mediaType?.includes('video') ? (
                      <video 
                        src={mediaUrl} 
                        className="w-full h-full object-cover"
                        controls
                        muted
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                        onEnded={handleVideoEnded}
                      />
                    ) : (
                      <img 
                        src={mediaUrl} 
                        alt="Ad content" 
                        className="w-full h-full object-cover"
                      />
                    )}
                    {mediaType?.includes('video') && !isVideoPlaying && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black bg-opacity-30 rounded-full p-2">
                          <Play className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                    {mediaType?.includes('video') && isVideoPlaying && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black bg-opacity-30 rounded-full p-2">
                          <Pause className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Navigation and control buttons */}
                <div className="absolute top-2 right-2 flex gap-1 z-20">
                  {!hasCatalog && (
                    <button
                      onClick={() => mediaInputRef.current?.click()}
                      disabled={uploadingMedia || loadingImage || deletingMedia}
                      className={`w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg ${
                        (uploadingMedia || loadingImage || deletingMedia) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Agregar más imágenes"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                  {!hasCatalog && (
                    <button
                      onClick={removeCurrentImage}
                      disabled={uploadingMedia || loadingImage || deletingMedia}
                      className={`w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg ${
                        (uploadingMedia || loadingImage || deletingMedia) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Eliminar imagen"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:bg-gray-50">
                <div className="w-16 h-16 text-gray-400 mb-4">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="text-center">
                  <div className={`${hasCatalog ? 'text-gray-500' : 'bg-blue-600 text-white rounded-lg w-4/5 mx-auto py-2'}`}>
                    <span className={`text-xs px-4 py-1.5 font-bold style={{ lineHeight: '2.5' }}`}>
                      {hasCatalog ? 'Usando imágenes del catálogo' : '¡Subí acá tus anuncios! Las dimensiones recomendadas son Cuadradas (1080x1080) y Verticales (1080x1920)'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions and Text Content - Scrollable */}
          <div className="flex flex-col max-h-48 overflow-y-auto">
            {/* Actions */}
            <div className="p-2 border-b border-gray-100">
              <div className="flex items-center space-x-3 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </div>
              
              <div className="font-semibold text-xs mb-1">{likes} likes</div>
            </div>
            
            {/* Business Name and Title */}
            <div className="p-2">
              <div className="flex items-center justify-between mb-1">
                {editingHeadline === 0 ? (
                  <div className="flex-1 relative z-20">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={localHeadlineText}
                        onChange={(e) => {
                          setLocalHeadlineText(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveHeadlineLocal();
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                        className="flex-1 text-xs p-1 border border-blue-300 rounded"
                        maxLength={HEADLINE_MAX_CHARS}
                        autoFocus
                        style={{ pointerEvents: 'auto' }}
                      />
                      <span className="text-[10px] text-gray-500 w-8 text-right">
                        {localHeadlineText.length}/{HEADLINE_MAX_CHARS}
                      </span>
                    </div>
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={handleSaveHeadlineLocal}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-xs font-semibold text-gray-900 flex-1">
                      {title}
                    </div>
                    {isEditMode && allowHeadlineEdit && (
                      <button 
                        onClick={() => handleEditHeadline(0, title || '')}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center justify-between">
                {editingDescription === 0 ? (
                  <div className="flex-1 relative z-20">
                    <div className="flex items-start gap-2">
                      <textarea
                        value={localDescriptionText}
                        onChange={(e) => {
                          setLocalDescriptionText(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            handleSaveDescriptionLocal();
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                        className="flex-1 text-xs p-1 border border-green-300 rounded resize-none"
                        maxLength={DESCRIPTION_MAX_CHARS}
                        rows={2}
                        autoFocus
                        style={{ pointerEvents: 'auto' }}
                      />
                      <span className="text-[10px] text-gray-500 w-8 text-right mt-1">
                        {localDescriptionText.length}/{DESCRIPTION_MAX_CHARS}
                      </span>
                    </div>
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={handleSaveDescriptionLocal}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-gray-600 flex-1">
                      {description}
                    </div>
                    {isEditMode && allowDescriptionEdit && (
                      <button 
                        onClick={() => handleEditDescription(0, description || '')}
A                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};

export default InstagramFeedPreview; 